import type { FC } from 'react'

import { LoaderCircle, Swipeable } from '@skytech/ui'
import { useNavigate, useParams } from 'react-router-dom'

import { useAppContext } from '@/App.context'
import InvoiceList from '@/components/invoice/InvoiceList'
import SalaryDeductionList from '@/components/salary-deduction/SalaryDeductionList'
import useInvoices from '@/hooks/invoices'
import useSetup from '@/hooks/setup'
import useSubscriptionServices from '@/hooks/subscriptionServices'
import { useProfile } from '@/hooks/useProfile'
import useUser from '@/hooks/user'
import { dateToMonthName, getYearFromDateString } from '@/util/formatters/dates'

import { Translation } from '../i18n/Translation'

import Graph from './Graph'
import { getDeadline } from './helpers'
import InvoiceChange from './InvoiceChange'
import InvoiceSummary from './InvoiceSummary'
import SalaryDeductionSummary from './SalaryDeductionSummary'

import './style/dashboard.scss'

const Dashboard = () => {
  const params = useParams()
  const { profile, isProfileLoading } = useProfile()

  const invoices = useInvoices()
  const setup = useSetup()
  const user = useUser()

  const subscriptionServices = useSubscriptionServices()
  const { whiteLabel } = useAppContext()

  if (isProfileLoading) return <LoaderCircle />

  if (!user || !profile) return null

  if (!profile.allowSubscriptions) {
    return (
      <div id="dashboard px-auto">
        <div className="content">
          <div id="no-data" className="section">
            <Translation id="This customer has disabled subscriptions" />
          </div>
        </div>
      </div>
    )
  }

  if (!invoices || invoices.length === 0) {
    return (
      <div id="dashboard px-auto">
        <div className="content">
          <div id="no-data" className="section">
            <Translation id="Finner ingen fakturadata for dette nummeret." />
          </div>
        </div>
      </div>
    )
  }

  const getOffset = () => (params.offset ? Number(params.offset) : 0)
  const currentInvoice = invoices ? invoices[getOffset()] : null
  const deadline = getDeadline({ setup, invoice: currentInvoice })

  return (
    <div id="dashboard" className={whiteLabel}>
      <div className="content">
        <Period />

        <SalaryDeductionSummary
          currentInvoice={currentInvoice}
          offset={getOffset()}
          deadline={deadline}
        />

        <InvoiceSummary currentInvoice={currentInvoice} offset={getOffset()} />

        {/* Legacy class */}
        {/* @ts-ignore */}
        <InvoiceChange currentInvoice={currentInvoice} offset={getOffset()} invoices={invoices} />

        <Graph offset={getOffset()} />

        <SalaryDeductionList
          currentInvoice={currentInvoice}
          offset={getOffset()}
          active={params.subSection === 'salary-deduction'}
          deadline={deadline}
          whiteLabel={whiteLabel}
          user={user}
          invoices={invoices}
          setup={setup}
          subscriptionServices={subscriptionServices}
        />

        <InvoiceList
          currentInvoice={currentInvoice}
          offset={getOffset()}
          active={params.subSection === 'invoice'}
          whiteLabel={whiteLabel}
          user={user}
          invoices={invoices}
          setup={setup}
          subscriptionServices={subscriptionServices}
        />
      </div>
    </div>
  )
}

export default Dashboard

const Prev: FC = () => {
  const params = useParams()
  const navigate = useNavigate()

  const invoices = useInvoices()

  const offset = params.offset ? Number(params.offset) : 0

  const changeOffset = (val: number) => {
    const updatedOffset = offset + val
    navigate('/dashboard/' + updatedOffset)
  }

  if (!invoices || offset >= invoices.length - 1) return null

  return (
    <Swipeable
      className="button no-select left"
      onClick={() => changeOffset(1)}
      onSwipedRight={() => changeOffset(1)}
    >
      &nbsp;
    </Swipeable>
  )
}

const Next: FC = () => {
  const params = useParams()
  const navigate = useNavigate()

  const invoices = useInvoices()

  const offset = params.offset ? Number(params.offset) : 0

  const changeOffset = (val: number) => {
    const updatedOffset = offset + val
    navigate('/dashboard/' + updatedOffset)
  }

  if (!invoices || offset === 0) return null

  return (
    <Swipeable
      className="button no-select right"
      onClick={() => changeOffset(-1)}
      onSwipedLeft={() => changeOffset(-1)}
    >
      &nbsp;
    </Swipeable>
  )
}

export const Period = () => {
  const params = useParams()
  const { whiteLabel } = useAppContext()

  const invoices = useInvoices()

  const offset = params.offset ? Number(params.offset) : 0
  const invoice = invoices ? invoices[offset] : null

  if (!invoices || !invoice) return null

  return (
    <div id="period" className={'section no-select ' + whiteLabel}>
      <Prev />

      <Next />

      <div>
        <span className={offset < invoices.length - 1 ? 'left_arrow' : 'is-hidden'}>‹</span>
        <Translation id={dateToMonthName(invoice.date)} /> {getYearFromDateString(invoice.date)}
        <span className={offset > 0 ? 'right_arrow' : 'is-hidden'}>›</span>
      </div>
    </div>
  )
}
